import { computed } from 'vue';
import { t } from '@/utils/i18n-helper';
import { withPerfilCredentials } from '@/services/perfil/perfilApiClient';

const normalizeText = value => {
    if (value === null || value === undefined) return null;

    const text = String(value).trim();
    return text.length ? text : null;
};

const parseBooleanish = value => {
    if (typeof value === 'boolean') return value;
    if (value === 1 || value === '1') return true;
    if (value === 0 || value === '0') return false;
    return null;
};

const normalizeResponsable = item => {
    if (!item || typeof item !== 'object') return null;

    const nombre = normalizeText(item?.nombre_completo ?? item?.nombre);
    if (!nombre) return null;

    return {
        nombre,
        dni: normalizeText(item?.dni) ?? null,
        email: normalizeText(item?.email) ?? null,
        telefono: normalizeText(item?.telefono) ?? null,
        fecha_nacimiento: normalizeText(item?.fecha_nacimiento) ?? null,
    };
};

const normalizePositions = value => {
    if (!Array.isArray(value)) return [];

    return value
        .map(item => normalizeText(item?.nombre ?? item))
        .filter(Boolean);
};

export function useCanteraCard(deportista) {
    const labels = computed(() => ({
        athlete: t('perfil.cantera_deportista', {}, 'Deportista'),
        active: t('perfil.activo', {}, 'Activo'),
        inactive: t('perfil.no_activo', {}, 'No activo'),
        renewal: t('perfil.cantera_renovacion', {}, 'Renovacion'),
        noRenewal: t('perfil.cantera_sin_renovacion', {}, 'Sin renovacion'),
        list: t('perfil.cantera_lista', {}, 'Lista'),
        inList: t('perfil.cantera_en_lista', {}, 'En lista'),
        category: t('perfil.cantera_categoria', {}, 'Categoria'),
        team: t('perfil.cantera_equipo', {}, 'Equipo'),
        laterality: t('perfil.cantera_lateralidad', {}, 'Lateralidad'),
        shirt: t('perfil.cantera_camiseta', {}, 'Camiseta'),
        positions: t('perfil.cantera_posiciones', {}, 'Posiciones'),
        primaryPositions: t('perfil.cantera_posiciones_principales', {}, 'Principales'),
        alternativePositions: t('perfil.cantera_posiciones_alternativas', {}, 'Alternativas'),
        responsables: t('perfil.cantera_responsables', {}, 'Responsables'),
        noResponsables: t('perfil.cantera_sin_responsables', {}, 'Sin responsables registrados'),
        nombre: t('perfil.nombre', {}, 'Nombre'),
        dni: t('perfil.dni', {}, 'DNI'),
        email: t('perfil.email', {}, 'Email'),
        telefono: t('perfil.telefono', {}, 'Teléfono'),
        fecha_nacimiento: t('perfil.fecha_nacimiento', {}, 'Fecha de nacimiento'),
        indumentaria: t('perfil.cantera_indumentaria', {}, 'Indumentaria'),
        size: t('perfil.cantera_talla', {}, 'Talla'),
        collected: t('perfil.cantera_indumentaria_recogida', {}, 'Recogida'),
        pendingCollection: t('perfil.cantera_indumentaria_pendiente', {}, 'Pendiente'),
        noIndumentaria: t('perfil.cantera_sin_indumentaria', {}, 'Sin indumentaria registrada'),
        plazosHelp: t('perfil.cantera_plazos_help', {}, 'Consulta y paga los plazos pendientes del deportista.'),
        downloadPdf: t('perfil.cantera_descargar_recibo', {}, 'Descargar recibo'),
        downloading: t('perfil.descargando', {}, 'Descargando...'),
        retry: t('perfil.cesion_reintentar', {}, 'Reintentar'),
    }));

    const nombreCompleto = computed(() =>
        normalizeText(deportista.value?.nombre_completo) ?? labels.value.athlete
    );

    const categoria = computed(() => normalizeText(deportista.value?.categoria));
    const equipo = computed(() => normalizeText(deportista.value?.equipo));
    const lateralidad = computed(() => normalizeText(deportista.value?.lateralidad));

    const listaRaw = computed(() => deportista.value?.lista);
    const listaText = computed(() => {
        const parsed = parseBooleanish(listaRaw.value);
        if (parsed !== null) return null;
        return normalizeText(listaRaw.value);
    });
    const listaBoolean = computed(() => parseBooleanish(listaRaw.value));

    const activo = computed(() => parseBooleanish(deportista.value?.activo));
    const renovacion = computed(() => parseBooleanish(deportista.value?.renovacion));

    const nPlazos = computed(() => Number(deportista.value?.n_plazos) || 0);
    const plazosLabel = computed(() => {
        if (!nPlazos.value) return null;
        return t('perfil.cantera_plazo_count', { count: nPlazos.value });
    });

    const camisetaNumero = computed(() => normalizeText(deportista.value?.camiseta_numero));
    const camisetaNombre = computed(() => normalizeText(deportista.value?.camiseta_nombre));
    const camisetaLabel = computed(() => {
        const parts = [];

        if (camisetaNumero.value) {
            parts.push(`#${camisetaNumero.value}`);
        }

        if (camisetaNombre.value) {
            parts.push(camisetaNombre.value);
        }

        return parts.join(' · ') || null;
    });

    const summaryContext = computed(() => {
        const parts = [categoria.value, equipo.value, plazosLabel.value].filter(Boolean);
        return parts.join(' · ');
    });

    const summaryBadges = computed(() => {
        const badges = [];

        if (activo.value !== null) {
            badges.push({
                key: 'activo',
                variant: activo.value ? 'success' : 'default',
                label: activo.value ? labels.value.active : labels.value.inactive,
                icon: activo.value ? 'check_circle' : 'cancel',
            });
        }

        if (renovacion.value) {
            badges.push({
                key: 'renovacion',
                variant: 'info',
                label: labels.value.renewal,
                icon: 'autorenew',
            });
        }

        if (listaBoolean.value) {
            badges.push({
                key: 'lista',
                variant: 'warning',
                label: labels.value.inList,
                icon: 'format_list_bulleted',
            });
        }

        return badges;
    });

    const expandedBadges = computed(() => {
        const badges = [...summaryBadges.value];

        if (renovacion.value === false) {
            badges.push({
                key: 'sin-renovacion',
                variant: 'default',
                label: labels.value.noRenewal,
                icon: 'remove_circle_outline',
            });
        }

        return badges;
    });

    const detailBadges = computed(() => {
        const summaryKeys = new Set(summaryBadges.value.map(badge => badge.key));
        return expandedBadges.value.filter(badge => !summaryKeys.has(badge.key));
    });

    const quickFacts = computed(() => {
        return [
            { key: 'lateralidad', label: labels.value.laterality, value: lateralidad.value },
            { key: 'camiseta', label: labels.value.shirt, value: camisetaLabel.value },
            { key: 'lista', label: labels.value.list, value: listaText.value },
        ].filter(item => item.value);
    });

    const posicionesPrincipales = computed(() => normalizePositions(deportista.value?.posiciones_principales));
    const posicionesAlternativas = computed(() => normalizePositions(deportista.value?.posiciones_alternativas));

    const responsables = computed(() => {
        if (!Array.isArray(deportista.value?.responsables)) return [];
        return deportista.value.responsables
            .map(item => normalizeResponsable(item))
            .filter(Boolean);
    });

    const indumentarias = computed(() => {
        if (!Array.isArray(deportista.value?.indumentarias)) return [];

        return deportista.value.indumentarias
            .map((item, index) => {
                const nombre = normalizeText(item?.nombre);
                if (!nombre) return null;

                const talla = normalizeText(item?.talla);
                const cantidad = Number(item?.cantidad) || 0;
                const meta = [
                    talla ? `${labels.value.size} ${talla}` : null,
                    cantidad > 0 ? t('perfil.cantera_ud_count', { count: cantidad }) : null,
                ].filter(Boolean).join(' · ');

                const badges = [];
                const recogida = parseBooleanish(item?.recogida);

                if (recogida !== null) {
                    badges.push({
                        key: `${item?.id ?? index}-recogida`,
                        variant: recogida ? 'info' : 'warning',
                        label: recogida ? labels.value.collected : labels.value.pendingCollection,
                        icon: recogida ? 'checkroom' : 'schedule',
                    });
                }

                return {
                    id: item?.id ?? `${nombre}-${index}`,
                    nombre,
                    imagen: normalizeText(item?.imagen ?? item?.imagen_formatted),
                    meta: meta || null,
                    badges,
                };
            })
            .filter(Boolean);
    });

    const hasPositions = computed(() =>
        posicionesPrincipales.value.length > 0 || posicionesAlternativas.value.length > 0
    );

    const hasResponsables = computed(() => responsables.value.length > 0);
    const hasIndumentarias = computed(() => indumentarias.value.length > 0);

    const downloadUrl = computed(() => {
        const raw = deportista.value?.url_recibo;
        return raw ? withPerfilCredentials(raw) : null;
    });

    return {
        labels,
        nombreCompleto,
        summaryContext,
        summaryBadges,
        detailBadges,
        quickFacts,
        posicionesPrincipales,
        posicionesAlternativas,
        responsables,
        indumentarias,
        hasPositions,
        hasResponsables,
        hasIndumentarias,
        downloadUrl,
    };
}
